import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, InputBase, Typography } from '@mui/material';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { Stop } from '../../types';
import { Board, formatDay } from '../../lib/plan';
import PlaceCard, { DRAG_TYPE, MoveTarget } from './PlaceCard';

interface Props {
  board: Board;
  targets: MoveTarget[];
  onPlace: (stopId: string, dayId: string | null, beforeId: string | null) => void;
  onToggleMustSee: (stop: Stop) => void;
  onRemove: (stop: Stop) => void;
  onAdd: (title: string) => void;
  onResearch: () => void;
}

type Filter = { kind: 'open' } | { kind: 'all' } | { kind: 'must' } | { kind: 'town'; town: string };

/**
 * Every place without a day. Dropping a placed card here takes it back off
 * its day, so the panel is also the way to undo a placement.
 */
export default function CandidatesPanel({ board, targets, onPlace, onToggleMustSee, onRemove, onAdd, onResearch }: Props) {
  const [filter, setFilter] = useState<Filter>({ kind: 'open' });
  const [quick, setQuick] = useState('');
  const [over, setOver] = useState(false);

  const candidates = useMemo(() => board.stops.filter((s) => !s.day_id && s.stop_type !== 'transit'), [board.stops]);
  const dayLabel = useMemo(() => {
    const m = new Map<string, string>();
    board.days.forEach((d, i) => m.set(d.day_id, `Day ${i + 1} · ${formatDay(d.date)}`));
    return m;
  }, [board.days]);

  const towns = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of candidates) if (c.city) counts.set(c.city, (counts.get(c.city) ?? 0) + 1);
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [candidates]);

  const open = candidates.filter((c) => !c.suggested_day_id);
  const shown = candidates
    .filter((c) => {
      switch (filter.kind) {
        case 'open':
          return !c.suggested_day_id;
        case 'must':
          return c.must_see;
        case 'town':
          return c.city === filter.town;
        default:
          return true;
      }
    })
    // Must-sees first, then the ones with a reason (the plan's leftovers), then the rest.
    .sort((a, b) => Number(b.must_see) - Number(a.must_see) || Number(!!b.plan_note) - Number(!!a.plan_note));

  const is = (f: Filter) => filter.kind === f.kind && (f.kind !== 'town' || (filter.kind === 'town' && filter.town === f.town));
  const chip = (f: Filter, label: string) => (
    <Chip
      key={label}
      label={label}
      size="small"
      onClick={() => setFilter(f)}
      color={is(f) ? 'primary' : 'default'}
      variant={is(f) ? 'filled' : 'outlined'}
    />
  );

  return (
    <Box
      component="aside"
      aria-labelledby="cands-title"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes(DRAG_TYPE)) {
          e.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const id = e.dataTransfer.getData(DRAG_TYPE);
        if (id) onPlace(id, null, null);
      }}
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: over ? 'secondary.main' : 'divider',
        borderRadius: 2,
        bgcolor: over ? '#FBF1EC' : '#FBF9F6',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        // Stays in view while the tree scrolls past it.
        position: { md: 'sticky' },
        top: { md: 16 },
        maxHeight: { md: 'calc(100vh - 32px)' },
        overflowY: { md: 'auto' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography id="cands-title" component="h2" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
          Candidates
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {candidates.length} · {candidates.filter((c) => c.must_see).length} must-see
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="outlined" startIcon={<TravelExploreIcon />} onClick={onResearch} sx={{ flex: 1 }}>
          Research more
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ContentPasteIcon />}
          component={RouterLink}
          to={`/trips/${board.trip.trip_id}/paste`}
          sx={{ flex: 1 }}
        >
          Paste a list
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {chip({ kind: 'open' }, `Not in plan · ${open.length}`)}
        {chip({ kind: 'all' }, `All · ${candidates.length}`)}
        {chip({ kind: 'must' }, 'Must-sees')}
        {towns.map(([t, n]) => chip({ kind: 'town', town: t }, `${t} · ${n}`))}
      </Box>

      <InputBase
        placeholder="+ Add a place"
        value={quick}
        onChange={(e) => setQuick(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && quick.trim()) {
            onAdd(quick.trim());
            setQuick('');
          }
        }}
        inputProps={{ 'aria-label': 'Add a place' }}
        sx={{ px: 1.25, py: 0.5, minHeight: 40, borderRadius: 1.5, border: '1px dashed', borderColor: 'divider', fontSize: '0.875rem' }}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {shown.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            {candidates.length === 0
              ? 'No candidates yet. Research places, paste a list, or add your own.'
              : filter.kind === 'open'
                ? 'Every candidate is on a day or suggested for one.'
                : 'Nothing matches this filter.'}
          </Typography>
        ) : (
          shown.map((c) => (
            <PlaceCard
              key={c.stop_id}
              stop={c}
              variant="candidate"
              targets={targets}
              suggestedLabel={c.suggested_day_id ? dayLabel.get(c.suggested_day_id) ?? null : null}
              onMove={(id, dayId) => onPlace(id, dayId, null)}
              onToggleMustSee={onToggleMustSee}
              onRemove={onRemove}
            />
          ))
        )}
      </Box>
      {over ? (
        <Typography variant="caption" color="secondary.dark" sx={{ textAlign: 'center' }}>
          Drop to take it off its day
        </Typography>
      ) : null}
    </Box>
  );
}
